import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { usd } from "@/lib/pricing";
import { toast } from "sonner";
import { Check, X, Eye, AlertTriangle, Mail, RotateCcw } from "lucide-react";
import { setBookingStatus, resendBookingEmail, refundBooking } from "@/lib/bookings.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/admin/")({
  component: AdminBookings,
});

type Booking = any;
type Car = { id: string; make: string; model: string; year: number };

function AdminBookings() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [cars, setCars] = useState<Record<string, Car>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "declined" | "refunded">("pending");
  const [busyId, setBusyId] = useState<string | null>(null);
  const updateBookingStatus = useServerFn(setBookingStatus);
  const resendEmailFn = useServerFn(resendBookingEmail);
  const refundFn = useServerFn(refundBooking);

  const load = async () => {
    setLoading(true);
    const { data: bs } = await supabase.from("bookings").select("*").order("created_at", { ascending: false });
    const { data: cs } = await supabase.from("cars").select("id,make,model,year");
    setBookings(bs || []);
    const m: Record<string, Car> = {};
    (cs || []).forEach((c: any) => { m[c.id] = c; });
    setCars(m);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id: string, status: "approved" | "declined" | "cancelled" | "completed" | "pending") => {
    setBusyId(id);
    try {
      await updateBookingStatus({ data: { bookingId: id, status } });
      track(status === "approved" ? "booking_approved" : status === "declined" ? "booking_declined" : "booking_status_changed", {
        booking_id: id,
        status,
      });
      toast.success(`Booking ${status}${status === "approved" ? " — pickup email sent" : ""}`);
      load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to update booking");
    } finally {
      setBusyId(null);
    }
  };

  const resend = async (id: string, kind: "request-received" | "approved" | "declined" | "owner-new-booking", label: string) => {
    setBusyId(id);
    try {
      await resendEmailFn({ data: { bookingId: id, kind } });
      toast.success(`${label} email re-queued`);
    } catch (e: any) {
      toast.error(e?.message || "Failed to resend email");
    } finally { setBusyId(null); }
  };

  const refund = async (id: string) => {
    if (!confirm("Refund this booking via Stripe? This cancels the booking and emails the renter.")) return;
    setBusyId(id);
    try {
      const env = getStripeEnvironment();
      const res: any = await refundFn({ data: { bookingId: id, environment: env } });
      track("booking_refunded", { booking_id: id, already_refunded: !!res?.alreadyRefunded });
      track("booking_declined", { booking_id: id, refunded: true });
      toast.success(res?.alreadyRefunded ? "Already refunded" : "Refund issued — renter notified");
      load();
    } catch (e: any) {
      toast.error(e?.message || "Refund failed");
    } finally { setBusyId(null); }
  };

  const viewLicense = async (path: string) => {
    const { data } = await supabase.storage.from("licenses").createSignedUrl(path, 300);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
    else toast.error("Could not load license image");
  };

  const filtered = filter === "all" ? bookings : bookings.filter(b => b.status === filter);

  // Detect overlapping bookings on the same car among pending/approved
  const activeForOverlap = bookings.filter(b => b.status === "pending" || b.status === "approved");
  const overlapsById = new Map<string, Booking[]>();
  for (const b of activeForOverlap) {
    const others = activeForOverlap.filter(o =>
      o.id !== b.id && o.car_id === b.car_id &&
      b.start_date <= o.end_date && o.start_date <= b.end_date
    );
    if (others.length) overlapsById.set(b.id, others);
  }

  return (
    <div>
      <Card className="mb-6 p-4 bg-cream/50 border-accent/30">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display text-lg text-espresso">Standard Operating Procedures</div>
            <div className="text-xs text-muted-foreground">Reference guides for the team and customers</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a href="/docs/admin-portal-sop.pdf" target="_blank" rel="noopener">
              <Button size="sm" variant="outline">Admin Portal SOP (PDF)</Button>
            </a>
            <a href="/docs/client-booking-sop.pdf" target="_blank" rel="noopener">
              <Button size="sm" variant="outline">Client Booking SOP (PDF)</Button>
            </a>
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl text-espresso">Bookings</h1>
        <div className="flex gap-2 flex-wrap">
          {(["pending", "approved", "declined", "refunded", "all"] as const).map(f => (
            <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)}>{f}</Button>
          ))}
        </div>
      </div>


      {loading ? <p className="mt-6 text-muted-foreground">Loading…</p> :
        filtered.length === 0 ? <Card className="mt-6 p-8 text-center text-muted-foreground">No bookings.</Card> :
        <div className="mt-6 space-y-4">
          {filtered.map(b => {
            const car = cars[b.car_id];
            const overlaps = overlapsById.get(b.id);
            return (
              <Card key={b.id} className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="font-display text-xl text-espresso">{car ? `${car.year} ${car.make} ${car.model}` : "Unknown car"}</div>
                    <div className="text-sm text-muted-foreground">{b.start_date} → {b.end_date} · {b.days} day{b.days===1?"":"s"}</div>
                    <div className="mt-2 text-sm">
                      <strong>{b.renter_name}</strong> · {b.renter_email} · {b.renter_phone}
                    </div>
                    <div className="text-xs text-muted-foreground">DOB {b.renter_dob} · Lic# {b.license_number}</div>
                    {b.notes && <div className="mt-2 text-sm italic text-espresso/80">"{b.notes}"</div>}
                    {overlaps && overlaps.length > 0 && (
                      <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <div>
                          <div className="font-semibold">Overlaps {overlaps.length} other {overlaps.length === 1 ? "booking" : "bookings"} on this car</div>
                          <ul className="mt-1 space-y-0.5">
                            {overlaps.map(o => (
                              <li key={o.id}>{o.status}: {o.start_date} → {o.end_date} ({o.renter_name})</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    <div className="mt-3 flex gap-2">
                      {b.license_front_url && <Button size="sm" variant="outline" onClick={() => viewLicense(b.license_front_url)}><Eye className="mr-1 h-3 w-3" /> Front</Button>}
                      {b.license_back_url && <Button size="sm" variant="outline" onClick={() => viewLicense(b.license_back_url)}><Eye className="mr-1 h-3 w-3" /> Back</Button>}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={b.status === "pending" ? "secondary" : b.status === "approved" ? "default" : "destructive"}>{b.status}</Badge>
                    <div className="mt-2 font-display text-2xl text-accent">{usd(Number(b.total))}</div>
                    <div className="text-xs text-muted-foreground">
                      {b.payment_status === "paid" ? "✓ Paid" : b.payment_status === "refunded" ? "Refunded" : b.payment_status || "—"}
                    </div>
                    {b.status === "pending" && (
                      <div className="mt-3 flex gap-2 justify-end flex-wrap">
                        <Button size="sm" disabled={busyId === b.id} onClick={() => updateStatus(b.id, "approved")} className="bg-primary"><Check className="mr-1 h-3 w-3" /> Approve</Button>
                        <Button size="sm" variant="destructive" disabled={busyId === b.id} onClick={() => b.payment_status === "paid" ? refund(b.id) : updateStatus(b.id, "declined")}>
                          <X className="mr-1 h-3 w-3" />
                          {b.payment_status === "paid" ? "Decline & refund" : "Decline"}
                        </Button>
                      </div>
                    )}
                    {b.payment_status === "paid" && b.status !== "cancelled" && b.payment_status !== "refunded" && (
                      <div className="mt-2 flex gap-2 justify-end flex-wrap">
                        <Button size="sm" variant="outline" disabled={busyId === b.id} onClick={() => refund(b.id)}>
                          Refund
                        </Button>
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap gap-1.5 justify-end border-t pt-2">
                      <span className="text-[10px] text-muted-foreground self-center mr-1">Resend:</span>
                      {b.payment_status === "paid" && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={busyId === b.id} onClick={() => resend(b.id, "request-received", "Confirmation")}>
                          <RotateCcw className="mr-1 h-3 w-3" /> Confirmation
                        </Button>
                      )}
                      {b.status === "approved" && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={busyId === b.id} onClick={() => resend(b.id, "approved", "Pickup")}>
                          <RotateCcw className="mr-1 h-3 w-3" /> Pickup
                        </Button>
                      )}
                      {(b.status === "declined" || b.status === "cancelled" || b.payment_status === "refunded") && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={busyId === b.id} onClick={() => resend(b.id, "declined", "Decline/refund")}>
                          <RotateCcw className="mr-1 h-3 w-3" /> Decline
                        </Button>
                      )}
                      {b.payment_status === "paid" && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={busyId === b.id} onClick={() => resend(b.id, "owner-new-booking", "Owner notification")}>
                          <Mail className="mr-1 h-3 w-3" /> Notify me
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      }
    </div>
  );
}
